import React, { useState, memo, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { navigation, siteConfig } from "@/data/mock";
import { cn } from "@/lib/utils";

export const Header = memo(() => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const toggleMobileMenu = useCallback(() => setMobileMenuOpen(prev => !prev), []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Simple check if current page
  const isActive = (href) => {
    const path = href.split("#")[0] || "/";
    return location.pathname === path;
  };

  // Get clean href without hash for Link
  const getHref = (href) => href.split("#")[0] || "/";

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      scrolled 
        ? "bg-white shadow-lg border-b border-slate-100" 
        : "bg-white/95 backdrop-blur-md"
    )}>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:bg-amber-600 transition-all duration-300 group-hover:scale-105">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-bold text-slate-900 text-lg tracking-tight hidden sm:block">
              {siteConfig.companyName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={getHref(item.href)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200",
                  isActive(item.href)
                    ? "text-amber-600 bg-amber-50"
                    : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                )}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-500 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Button 
              asChild 
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-5 rounded-xl font-semibold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 transition-all duration-300 group"
            >
              <Link to="/contact">
                Get Started
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2.5 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-xl animate-fade-in-up">
            <div className="px-4 py-6 space-y-1">
              {navigation.map((item, i) => (
                <Link
                  key={item.name}
                  to={getHref(item.href)}
                  onClick={closeMobileMenu}
                  className={cn(
                    "flex items-center px-4 py-3.5 text-base font-medium rounded-xl transition-all",
                    isActive(item.href)
                      ? "text-amber-600 bg-amber-50"
                      : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                  )}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <span className="ml-auto w-2 h-2 bg-amber-500 rounded-full" />
                  )}
                </Link>
              ))}
              <div className="pt-4 mt-4 border-t border-slate-100">
                <Button 
                  asChild 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6 rounded-xl font-semibold shadow-lg"
                >
                  <Link to="/contact" onClick={closeMobileMenu}>
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
});
